import { db } from '../config';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, increment, Timestamp, addDoc, deleteDoc } from 'firebase/firestore';

// Discount types
export interface Discount {
  id: string;
  type: 'percentage' | 'fixed' | 'duration_free' | 'tiered';
  amount: number;
  isActive: boolean;
  startDate: Date;
  endDate?: Date;
  applicableVesselTypes?: string[];
  
  // Duration-based discount fields
  freeDurationMinutes?: number; // Number of minutes that are free
  additionalHourPricing?: 'regular' | 'discounted' | 'fixed_rate'; // How to price additional hours
  additionalHourDiscount?: number; // Percentage discount on additional hours (if discounted)
  additionalHourRate?: number; // Fixed rate per hour for additional hours (if fixed_rate)
}

export interface PromoDiscount extends Discount {
  discountType: 'promo';
  code: string;
  name?: string; // Optional internal name for complex promo codes
  usageLimit?: number;
  usageCount: number;
  minPurchaseAmount?: number;
  userRestriction?: 'new' | 'returning' | 'none';
  // Time-based restrictions (optional)
  daysOfWeek?: string[];
  hoursOfDay?: number[];
}

// Time-based discounts are now consolidated into PromoDiscount

export interface UserDiscount extends Discount {
  discountType: 'user';
  name: string;
  userType: 'new' | 'returning' | 'loyal' | 'all';
  minBookings?: number;
  maxUses?: number;
  usageLimit?: number;
  usageCount: number;
}

/**
 * Calculates discount for duration-based promotions
 * @param discount The discount details
 * @param durationMinutes The total rental duration in minutes
 * @param hourlyRate The regular hourly rate for the vessel
 * @returns The calculated discount amount
 */
export const calculateDurationDiscount = (
  discount: PromoDiscount,
  durationMinutes: number,
  hourlyRate: number
): number => {
  if (discount.type === 'duration_free' && discount.freeDurationMinutes) {
    const freeDuration = Math.min(durationMinutes, discount.freeDurationMinutes);
    const freeAmount = (freeDuration / 60) * hourlyRate;
    
    // Calculate remaining time after free period
    const remainingMinutes = Math.max(0, durationMinutes - discount.freeDurationMinutes);
    let additionalDiscount = 0;
    
    if (remainingMinutes > 0 && discount.additionalHourPricing === 'discounted' && discount.additionalHourDiscount) {
      const remainingAmount = (remainingMinutes / 60) * hourlyRate;
      additionalDiscount = remainingAmount * (discount.additionalHourDiscount / 100);
    } else if (remainingMinutes > 0 && discount.additionalHourPricing === 'fixed_rate' && discount.additionalHourRate) {
      const regularRemainingAmount = (remainingMinutes / 60) * hourlyRate;
      const discountedRemainingAmount = (remainingMinutes / 60) * discount.additionalHourRate;
      additionalDiscount = Math.max(0, regularRemainingAmount - discountedRemainingAmount);
    }
    
    return freeAmount + additionalDiscount;
  }
  
  // Fallback to regular discount calculation
  if (discount.type === 'percentage') {
    return (hourlyRate * (durationMinutes / 60)) * (discount.amount / 100);
  } else if (discount.type === 'fixed') {
    return discount.amount;
  }
  
  return 0;
};

/**
 * Validates a promo code for a reservation
 * @param code The promo code to validate
 * @param userId The ID of the user making the reservation
 * @param totalAmount The total amount of the reservation
 * @param vesselType The type of vessel being reserved
 * @param bookingDate The date of the booking
 * @param durationMinutes The rental duration in minutes (optional, for duration-based discounts)
 * @param hourlyRate The hourly rate for the vessel (optional, for duration-based discounts)
 * @returns The discount amount and discount details if valid, error otherwise
 */
export const validatePromoCode = async (
  code: string, 
  userId: string, 
  totalAmount: number,
  vesselType: string,
  bookingDate: Date,
  durationMinutes?: number,
  hourlyRate?: number
): Promise<{ discount: number; discountType: 'percentage' | 'fixed' | 'duration_free' | 'tiered'; discountDetails: PromoDiscount } | { error: string }> => {
  try {
    console.log('validatePromoCode called with:', { code, userId, totalAmount, vesselType, bookingDate });
    
    // Query for promo codes
    const promoQuery = query(
      collection(db, 'discounts'),
      where('discountType', '==', 'promo'),
      where('isActive', '==', true)
    );
    
    console.log('Querying for all active promo codes');
    const snapshot = await getDocs(promoQuery);
    
    console.log('Active promo codes found:', snapshot.size);
    
    // Manually filter to find a case-insensitive match
    const matchingDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.code && (data.code.toUpperCase() === code.toUpperCase());
    });
    
    console.log('Matching promo codes after case-insensitive filter:', matchingDocs.length);
    
    if (matchingDocs.length === 0) {
      console.log('Promo code not found after case-insensitive check');
      return { error: 'Promo code not found' };
    }
    
    // Get the promo code data
    const promoDoc = matchingDocs[0];
    const promoData = promoDoc.data() as Omit<PromoDiscount, 'id' | 'startDate' | 'endDate'> & {
      startDate: Timestamp;
      endDate?: Timestamp;
    };
    
    console.log('Promo code found in database:', promoData);
    
    const promoCode: PromoDiscount = {
      id: promoDoc.id,
      ...promoData,
      startDate: promoData.startDate.toDate(),
      endDate: promoData.endDate ? promoData.endDate.toDate() : undefined
    };
    
    console.log('Converted promo code startDate:', promoCode.startDate);
    console.log('Converted promo code endDate:', promoCode.endDate);
    
    // Validate the promo code
    const now = new Date();
    console.log('Current date for comparison:', now);
    console.log('Current date in ISO format:', now.toISOString());
    
    // Ensure proper date comparison by normalizing to UTC strings and back to dates
    // This helps avoid time zone issues
    const startDateUTC = new Date(promoCode.startDate.toISOString());
    const endDateUTC = promoCode.endDate ? new Date(promoCode.endDate.toISOString()) : undefined;
    const nowUTC = new Date(now.toISOString());
    
    console.log('Normalized dates for comparison:');
    console.log('Start date UTC:', startDateUTC.toISOString());
    console.log('End date UTC:', endDateUTC ? endDateUTC.toISOString() : 'Not set');
    console.log('Current date UTC:', nowUTC.toISOString());
    
    // Check if the promo code is valid for the current date
    const isBeforeStartDate = nowUTC < startDateUTC;
    const isAfterEndDate = endDateUTC && nowUTC > endDateUTC;
    
    console.log('Date validation checks:', { 
      isBeforeStartDate, 
      isAfterEndDate,
      currentDateTimestamp: nowUTC.getTime(),
      startDateTimestamp: startDateUTC.getTime(),
      endDateTimestamp: endDateUTC ? endDateUTC.getTime() : 'N/A'
    });
    
    if (isBeforeStartDate) {
      console.log('Promo code invalid due to date restrictions - not yet active');
      const startDateStr = promoCode.startDate.toLocaleDateString();
      return { error: `Promo code is not active until ${startDateStr}` };
    }
    
    if (isAfterEndDate) {
      console.log('Promo code invalid due to date restrictions - expired');
      const endDateStr = promoCode.endDate!.toLocaleDateString();
      return { error: `Promo code expired on ${endDateStr}` };
    }
    
    // Check if the promo code has reached its usage limit
    if (promoCode.usageLimit !== undefined && promoCode.usageLimit !== null && promoCode.usageCount >= promoCode.usageLimit) {
      console.log('Promo code usage limit reached');
      return { error: 'Promo code usage limit has been reached' };
    }
    
    // Check if the booking amount meets the minimum purchase amount
    if (promoCode.minPurchaseAmount !== undefined && promoCode.minPurchaseAmount !== null && totalAmount < promoCode.minPurchaseAmount) {
      console.log('Booking amount does not meet minimum purchase requirement');
      return { error: `Minimum purchase amount of $${promoCode.minPurchaseAmount.toFixed(2)} required` };
    }
    
    // Check time-based restrictions (optional for all promo codes)
    const dayOfWeek = bookingDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const hourOfDay = bookingDate.getHours();
    
    console.log('Day-of-week validation:', {
      bookingDate: bookingDate.toDateString(),
      dayOfWeek,
      promoDaysOfWeek: promoCode.daysOfWeek,
      isDayAllowed: !promoCode.daysOfWeek || promoCode.daysOfWeek.length === 0 || promoCode.daysOfWeek.includes(dayOfWeek)
    });
    
    // Check if the promo code is restricted to specific days of the week
    if (promoCode.daysOfWeek && promoCode.daysOfWeek.length > 0 &&
        !promoCode.daysOfWeek.includes(dayOfWeek)) {
      // Sort days in chronological order (Monday first)
      const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const sortedValidDays = promoCode.daysOfWeek
        .sort((a, b) => dayOrder.indexOf(a.toLowerCase()) - dayOrder.indexOf(b.toLowerCase()))
        .map(day => day.charAt(0).toUpperCase() + day.slice(1));
      const validDaysText = sortedValidDays.join(', ');
      
      console.log(`Promo code rejected: booking is on ${dayOfWeek}, but only valid on: ${validDaysText}`);
      return { error: `Promo code only valid on: ${validDaysText}` };
    }
    
    // Check if the promo code is restricted to specific hours of the day
    if (promoCode.hoursOfDay && promoCode.hoursOfDay.length > 0 &&
        !promoCode.hoursOfDay.includes(hourOfDay)) {
      const validHours = promoCode.hoursOfDay.map(h => `${h}:00`).join(', ');
      return { error: `Promo code only valid during: ${validHours}` };
    }
    
    // Check if the promo code is applicable to the vessel type
    if (promoCode.applicableVesselTypes && promoCode.applicableVesselTypes.length > 0 &&
        !promoCode.applicableVesselTypes.includes(vesselType)) {
      console.log('Promo code not applicable to this vessel type');
      const validTypes = promoCode.applicableVesselTypes.join(', ');
      return { error: `Promo code only valid for: ${validTypes}` };
    }
    
    // TODO: Check user restriction based on booking history
    // This would require querying the user's booking history
    
    // Calculate the discount amount
    let discountAmount: number;
    if (promoCode.type === 'duration_free' && durationMinutes && hourlyRate) {
      discountAmount = calculateDurationDiscount(promoCode, durationMinutes, hourlyRate);
      console.log(`Calculated duration-based discount: ${promoCode.freeDurationMinutes} minutes free = $${discountAmount}`);
    } else if (promoCode.type === 'duration_free') {
      console.log('Duration-free discount missing required parameters:', {
        durationMinutes,
        hourlyRate,
        freeDurationMinutes: promoCode.freeDurationMinutes
      });
      return { error: 'Duration-based discount requires rental duration and hourly rate information. Please try again or contact support.' };
    } else if (promoCode.type === 'percentage') {
      discountAmount = (totalAmount * promoCode.amount) / 100;
      console.log(`Calculated percentage discount: ${promoCode.amount}% of $${totalAmount} = $${discountAmount}`);
    } else if (promoCode.type === 'fixed') {
      discountAmount = promoCode.amount;
      console.log(`Using fixed discount amount: $${discountAmount}`);
    } else if (promoCode.type === 'tiered') {
      console.log('Tiered discount type detected but not yet implemented');
      return { error: `Tiered discounts are not yet supported. Please contact support for assistance with promo code: ${code}` };
    } else {
      console.log(`Unknown discount type: ${promoCode.type}. Available types: percentage, fixed, duration_free, tiered`);
      console.log('Promo code configuration:', {
        type: promoCode.type,
        amount: promoCode.amount,
        freeDurationMinutes: promoCode.freeDurationMinutes,
        durationMinutes,
        hourlyRate
      });
      return { error: `Invalid discount configuration. Discount type "${promoCode.type}" is not supported. Please contact support for assistance.` };
    }
    
    // Return the discount amount and the discount details
    console.log('Promo code validation successful');
    return {
      discount: discountAmount,
      discountType: promoCode.type,
      discountDetails: promoCode
    };
  } catch (error) {
    console.error('Error validating promo code:', error);
    return { error: 'Failed to validate promo code. Please try again.' };
  }
};

/**
 * Applies a promo code to a reservation
 * @param promoCodeId The ID of the promo code to apply
 * @returns True if the promo code was successfully applied, false otherwise
 */
export const applyPromoCode = async (promoCodeId: string): Promise<boolean> => {
  try {
    // Increment the usage count of the promo code
    const promoRef = doc(db, 'discounts', promoCodeId);
    await updateDoc(promoRef, {
      usageCount: increment(1)
    });
    
    return true;
  } catch (error) {
    console.error('Error applying promo code:', error);
    return false;
  }
};

/**
 * Applies a discount (time-based or user-based) to a reservation
 * @param discountId The ID of the discount to apply
 * @returns True if the discount was successfully applied, false otherwise
 */
export const applyDiscount = async (discountId: string): Promise<boolean> => {
  try {
    // Increment the usage count of the discount
    const discountRef = doc(db, 'discounts', discountId);
    await updateDoc(discountRef, {
      usageCount: increment(1)
    });
    
    return true;
  } catch (error) {
    console.error('Error applying discount:', error);
    return false;
  }
};

// Time-based discounts are now promo code controlled and handled by validatePromoCode

/**
 * Gets all applicable user-based discounts for a user
 * @param userId The ID of the user
 * @param bookingCount The number of previous bookings the user has made
 * @param vesselType The type of vessel being booked
 * @returns An array of applicable user-based discounts
 */
export const getApplicableUserDiscounts = async (
  userId: string,
  bookingCount: number,
  vesselType: string
): Promise<UserDiscount[]> => {
  try {
    // Query for active user-based discounts
    const userQuery = query(
      collection(db, 'discounts'),
      where('discountType', '==', 'user'),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(userQuery);
    
    if (snapshot.empty) {
      return [];
    }
    
    const now = new Date();
    
    // Filter user-based discounts that are applicable to the user
    const applicableDiscounts: UserDiscount[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data() as Omit<UserDiscount, 'id' | 'startDate' | 'endDate'> & {
        startDate: Timestamp;
        endDate?: Timestamp;
      };
      
      const userDiscount: UserDiscount = {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate ? data.endDate.toDate() : undefined
      };
      
      // Check if the discount is valid for the current date
      if (now < userDiscount.startDate || (userDiscount.endDate && now > userDiscount.endDate)) {
        return; // Discount has expired or not yet valid
      }
      
      // Check if the discount has reached its usage limit
      if (userDiscount.usageLimit !== undefined && userDiscount.usageLimit !== null && userDiscount.usageCount >= userDiscount.usageLimit) {
        return; // Discount has reached its usage limit
      }
      
      // Check if the discount is applicable to the vessel type
      if (userDiscount.applicableVesselTypes && userDiscount.applicableVesselTypes.length > 0 &&
          !userDiscount.applicableVesselTypes.includes(vesselType)) {
        return; // Discount is not applicable to the vessel type
      }
      
      // Check if the discount is applicable to the user type
      if (userDiscount.userType === 'new' && bookingCount > 0) {
        return; // Discount is only for new users
      }
      
      if (userDiscount.userType === 'returning' && bookingCount === 0) {
        return; // Discount is only for returning users
      }
      
      if (userDiscount.userType === 'loyal' && 
          userDiscount.minBookings !== undefined && 
          bookingCount < userDiscount.minBookings) {
        return; // Discount is only for loyal users with minimum bookings
      }
      
      // TODO: Check if the user has already used this discount up to maxUses
      // This would require querying the user's discount usage history
      
      // Discount is applicable to the user
      applicableDiscounts.push(userDiscount);
    });
    
    return applicableDiscounts;
  } catch (error) {
    console.error('Error getting applicable user discounts:', error);
    return [];
  }
};

// calculateBestDiscount function removed - time-based discounts are now promo code controlled

// CRUD Operations for Discounts Management

export type AllDiscountTypes = PromoDiscount | UserDiscount;

/**
 * Gets all discounts
 * @returns Array of all discounts
 */
export const getAllDiscounts = async (): Promise<AllDiscountTypes[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'discounts'));
    const discounts: AllDiscountTypes[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data() as Omit<AllDiscountTypes, 'id' | 'startDate' | 'endDate'> & {
        startDate: Timestamp;
        endDate?: Timestamp;
      };
      
      const discount: AllDiscountTypes = {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate ? data.endDate.toDate() : undefined
      } as AllDiscountTypes;
      
      discounts.push(discount);
    });
    
    return discounts;
  } catch (error) {
    console.error('Error getting all discounts:', error);
    throw error;
  }
};

/**
 * Creates a new discount
 * @param discountData The discount data to create
 * @returns The ID of the created discount
 */
export const createDiscount = async (discountData: Omit<AllDiscountTypes, 'id'>): Promise<string> => {
  try {
    const dataToSave = {
      ...discountData,
      startDate: Timestamp.fromDate(discountData.startDate),
      endDate: discountData.endDate ? Timestamp.fromDate(discountData.endDate) : null
    };
    
    const docRef = await addDoc(collection(db, 'discounts'), dataToSave);
    return docRef.id;
  } catch (error) {
    console.error('Error creating discount:', error);
    throw error;
  }
};

/**
 * Updates an existing discount
 * @param id The ID of the discount to update
 * @param discountData The updated discount data
 */
export const updateDiscount = async (id: string, discountData: Partial<Omit<AllDiscountTypes, 'id'>>): Promise<void> => {
  try {
    const updateData: any = { ...discountData };
    
    if (discountData.startDate) {
      updateData.startDate = Timestamp.fromDate(discountData.startDate);
    }
    if (discountData.endDate) {
      updateData.endDate = Timestamp.fromDate(discountData.endDate);
    }
    
    const discountRef = doc(db, 'discounts', id);
    await updateDoc(discountRef, updateData);
  } catch (error) {
    console.error('Error updating discount:', error);
    throw error;
  }
};

/**
 * Deletes a discount
 * @param id The ID of the discount to delete
 */
export const deleteDiscount = async (id: string): Promise<void> => {
  try {
    const discountRef = doc(db, 'discounts', id);
    await deleteDoc(discountRef);
  } catch (error) {
    console.error('Error deleting discount:', error);
    throw error;
  }
};

/**
 * Gets discounts by type
 * @param type The type of discount to get
 * @returns Array of discounts of the specified type
 */
export const getDiscountsByType = async (type: 'promo' | 'time' | 'user'): Promise<AllDiscountTypes[]> => {
  try {
    const q = query(
      collection(db, 'discounts'),
      where('discountType', '==', type)
    );
    
    const snapshot = await getDocs(q);
    const discounts: AllDiscountTypes[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data() as Omit<AllDiscountTypes, 'id' | 'startDate' | 'endDate'> & {
        startDate: Timestamp;
        endDate?: Timestamp;
      };
      
      const discount: AllDiscountTypes = {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate ? data.endDate.toDate() : undefined
      } as AllDiscountTypes;
      
      discounts.push(discount);
    });
    
    return discounts;
  } catch (error) {
    console.error('Error getting discounts by type:', error);
    throw error;
  }
}; 